import { jwtDecode } from 'jwt-decode';

interface JWTPayload {
  CASGreenThreshold?: number;
  CASAmberThreshold?: number;
  realm_access?: {
    roles: string[];
  };
  // Add other JWT fields as needed
  sub?: string;
  iat?: number;
  exp?: number;
  iss?: string;
  aud?: string;
}

/**
 * Decode JWT token and extract payload
 */
export const decodeJWT = (token: string): JWTPayload | null => {
  try {
    return jwtDecode<JWTPayload>(token);
  } catch (error) {
    console.error('Failed to decode JWT token:', error);
    return null;
  }
};

/**
 * Get user role from JWT token
 * Returns 'Admin' if 'app-admin' role is present, otherwise 'Customer'
 */
export const getUserRole = (token: string): 'Admin' | 'Customer' => {
  const payload = decodeJWT(token);
  
  if (!payload || !payload.realm_access || !payload.realm_access.roles) {
    return 'Customer';
  }

  if (payload.realm_access.roles.includes('app-admin')) {
    return 'Admin';
  }

  return 'Customer';
};

/**
 * Get CAS thresholds from JWT token
 */
export const getCASThresholds = (token: string): { green: number; amber: number } | null => {
  const payload = decodeJWT(token);

  if (!payload || !payload.CASGreenThreshold || !payload.CASAmberThreshold) {
    console.warn('CAS thresholds not found in JWT token or invalid format');
    console.warn('Payload contents:', payload);
    return null;
  }
  
  return {
    green: Number(payload.CASGreenThreshold),
    amber: Number(payload.CASAmberThreshold)
  };
};

/**
 * Determine CAS index color based on score and thresholds
 */
export const getCASIndexColor = (score: number, thresholds: { green: number; amber: number }): 'green' | 'amber' | 'red' => {
  if (score >= thresholds.green) return 'green';
  if (score >= thresholds.amber) return 'amber';
  return 'red';
};

/**
 * Check if the token is expired
 */
export const isTokenExpired = (token: string): boolean => {
  const payload = decodeJWT(token);
  if (!payload || !payload.exp) {
    return true;
  }
  const currentTime = Math.floor(Date.now() / 1000);
  return payload.exp < currentTime;
};

/**
 * Validate JWT token structure and expiration
 */
export const isValidToken = (token: string): boolean => {
  const payload = decodeJWT(token);
  return !!payload && !isTokenExpired(token);
};
